import { useRef, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber'; // Added useThree
import { RigidBody, CuboidCollider, TrimeshCollider } from '@react-three/rapier';
import * as THREE from 'three';

export function Ring({ position, id, onRemove }) {
  const ringRef = useRef(); // Per al cos principal de l'aro
  const sensorRef = useRef(); // Per al RigidBody del sensor
  const meshRef = useRef(); // Ref for the mesh to access its material
  const { camera } = useThree(); // Get camera instance

  const geometry = useMemo(() => new THREE.TorusGeometry(1.5, 0.1, 16, 32), []);
  // Initialize material with transparent: true to allow opacity changes
  const material = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#ffff00",
    roughness: 0.8,
    transparent: true, // Important for opacity changes
  }), []);

  useFrame(() => { 
    // Ensure refs are available.
    if (!ringRef.current || !meshRef.current) return;

    const ringWorldPos = ringRef.current.translation();
    const cameraX = camera.position.x; // Get camera's current X position

    // Removal logic: if ring is far behind the camera
    if (ringWorldPos.x < cameraX - 20) {
      if (onRemove) { // Call onRemove only if it's provided
        onRemove(id);
      }
      return; // Early exit if the ring is removed
    }

    // Fog effect logic based on distance to camera's X position
    const distanceToCameraX = ringWorldPos.x - cameraX;

    // Define distances for fog effect:
    // FOG_NEAR: Rings closer than this distance are fully opaque.
    // FOG_FAR: Rings further than this distance have MIN_OPACITY.
    // Opacity is interpolated linearly between these distances.
    const FOG_NEAR = 20; // Rings within 20 units in front of camera are fully visible
    const FOG_FAR = 85;  // Rings 85 units or further get minimum opacity
    const MIN_OPACITY = 0.1; // Minimum opacity for the furthest rings

    let opacity;

    if (distanceToCameraX <= FOG_NEAR) {
      // Ring is close to the camera or behind FOG_NEAR threshold
      opacity = 1.0;
    } else if (distanceToCameraX >= FOG_FAR) {
      // Ring is beyond or at FOG_FAR threshold
      opacity = MIN_OPACITY;
    } else {
      // Ring is between FOG_NEAR and FOG_FAR, interpolate opacity
      const fadeProgress = (distanceToCameraX - FOG_NEAR) / (FOG_FAR - FOG_NEAR);
      opacity = 1.0 - fadeProgress * (1.0 - MIN_OPACITY);
    }
    
    // Apply the calculated opacity to the ring's material
    if (meshRef.current.material) {
      meshRef.current.material.opacity = THREE.MathUtils.clamp(opacity, MIN_OPACITY, 1.0);
    }
  });

  const sensorArgs = useMemo(() => {
    const innerHoleRadius = 1.5 - 0.1;
    return [innerHoleRadius * 0.9, innerHoleRadius * 0.9, 0.3];
  }, []);

  const trimeshArgs = useMemo(() => [
    geometry.attributes.position.array,
    geometry.index.array
  ], [geometry]);

  return (
    <>
      {/* Cos FÍSIC de l'Aro */}
      <RigidBody
        ref={ringRef}
        type="fixed"
        colliders={false}
        position={position}
        rotation-y={Math.PI / 2}
        userData={{ type: 'ringBodyContainer', ringId: id }} // userData per al cos, per si de cas
      >
        <mesh ref={meshRef} geometry={geometry} material={material} /> {/* Added ref={meshRef} to the mesh */}
        <TrimeshCollider
          args={trimeshArgs}
          userData={{ type: 'ringBodyActual', ringId: id, objectName: `RingBodyActual-${id}` }}
        />
      </RigidBody>

      {/* SENSOR de l'Aro com un RigidBody separat */}
      <RigidBody
        ref={sensorRef}
        type="fixed" // Encara fixed
        colliders={false} // Definim el collider fill explícitament
        position={position} // Mateixa posició que l'aro
        rotation-y={Math.PI / 2} // Mateixa rotació
        userData={{ type: 'ringSensorRB', ringId: id, objectName: `RingSensorRB-${id}` }} // UserData AL RIGIDBODY
      >
        <CuboidCollider
          args={sensorArgs}
          sensor // Encara és un sensor
          // No posem userData aquí, ja que ho hem posat al RigidBody pare del sensor
        />
      </RigidBody>
    </>
  );
}